/**
 * Icon dispatch: routes a `value` (or legacy `name`) string to either a
 * `lucide-react` component or an inline emoji span.
 *
 * Both props accept the SAME grammar (value wins when both are set):
 *   "lucide:<kebab-name>"  — render lucide-react component
 *   "emoji:<char>"         — render emoji glyph in a <span>
 *   "<bare-name>"          — legacy alias / kebab lucide name
 *   anything else / empty  — fallback to lucide:tag
 *
 * The grammar MUST be honored on both props: category rows persist
 * prefixed values (`lucide:pill`) after an IconPicker edit while seeded
 * system categories still carry bare aliases (`bag`), and call sites pass
 * `category.icon` through either prop. Splitting the parsing per-prop is
 * how every edited category once collapsed to the Tag glyph on all list
 * screens.
 */
import type { CSSProperties } from 'react';
import { LUCIDE_REGISTRY, TAG_FALLBACK, type LucideComponent } from './lucideRegistry';

export type IconName = string;

export interface IconProps {
  /** Preferred: prefixed value (lucide:<name> or emoji:<char>). */
  value?: string;
  /** Legacy: bare lucide name, used by app shell call sites. */
  name?: string;
  size?: number;
  stroke?: string;
  strokeWidth?: number;
  fill?: string;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
}

/**
 * Maps the 56 historical hand-coded icon names to their lucide-react kebab
 * equivalents. Bare `name` values that aren't in this map are treated as
 * already-valid lucide kebab names and looked up directly.
 *
 * Adding a new icon does NOT require an entry here — only pre-existing
 * call sites use these legacy aliases.
 */
export const LEGACY_ALIAS_MAP: Readonly<Record<string, string>> = {
  home: 'home',
  calendar: 'calendar',
  chart: 'line-chart',
  settings: 'settings',
  plus: 'plus',
  minus: 'minus',
  check: 'check',
  x: 'x',
  chevR: 'chevron-right',
  chevL: 'chevron-left',
  chevD: 'chevron-down',
  search: 'search',
  user: 'user',
  bell: 'bell',
  camera: 'camera',
  wallet: 'wallet',
  book: 'book',
  pie: 'pie-chart',
  trending: 'trending-up',
  trendingDown: 'trending-down',
  utensils: 'utensils',
  car: 'car',
  house: 'house',
  bag: 'shopping-bag',
  gamepad: 'gamepad-2',
  heart: 'heart',
  book2: 'book-open',
  gift: 'gift',
  briefcase: 'briefcase',
  repeat: 'repeat',
  faceid: 'scan-face',
  lock: 'lock',
  trash: 'trash-2',
  edit: 'pencil',
  cloud: 'cloud',
  cloudOff: 'cloud-off',
  filter: 'filter',
  dots: 'more-horizontal',
  note: 'sticky-note',
  tag: 'tag',
  paw: 'paw-print',
  receipt: 'receipt',
  creditcard: 'credit-card',
  tv: 'tv',
  plane: 'plane',
  cat: 'cat',
  pill: 'pill',
  tooth: 'smile',
  globe: 'globe',
  suitcase: 'luggage',
  music: 'music',
  film: 'film',
  hash: 'hash',
  percent: 'percent',
  coins: 'coins',
  clock: 'clock',
};

function resolveLucideComponent(input: string): LucideComponent | null {
  // Legacy aliases win over raw kebab lookup so values like `lucide:gamepad`
  // resolve to `Gamepad2` (matching prior hand-coded behavior) rather than
  // the simpler `Gamepad` glyph.
  const kebab = LEGACY_ALIAS_MAP[input] ?? input;
  return LUCIDE_REGISTRY[kebab] ?? null;
}

function lucideForLegacyOrKebab(input: string): LucideComponent {
  return resolveLucideComponent(input) ?? TAG_FALLBACK;
}

interface ParsedValue {
  kind: 'lucide' | 'emoji';
  payload: string;
}

function parseIconValue(raw: string | undefined): ParsedValue | null {
  if (!raw) return null;
  const colonAt = raw.indexOf(':');
  if (colonAt < 0) return null;
  const prefix = raw.slice(0, colonAt);
  const payload = raw.slice(colonAt + 1);
  if (prefix === 'lucide' && payload.length > 0) return { kind: 'lucide', payload };
  if (prefix === 'emoji' && payload.length > 0) return { kind: 'emoji', payload };
  return null;
}

export default function Icon({
  value,
  name,
  size = 20,
  stroke = 'currentColor',
  strokeWidth = 1.8,
  fill = 'none',
  className,
  style,
  'aria-label': ariaLabel,
}: IconProps): JSX.Element {
  // Same grammar on both props; `value` wins when both are set.
  const parsed = parseIconValue(value) ?? parseIconValue(name);

  if (parsed?.kind === 'emoji') {
    const spanStyle: CSSProperties = {
      fontSize: `${size}px`,
      lineHeight: 1,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      ...style,
    };
    return (
      <span
        className={className}
        style={spanStyle}
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : true}
      >
        {parsed.payload}
      </span>
    );
  }

  // Lucide path: prefixed `lucide:<name>`, or a bare legacy/kebab name
  // from either prop, or fallback.
  let Component: LucideComponent;
  if (parsed?.kind === 'lucide') {
    Component = resolveLucideComponent(parsed.payload) ?? TAG_FALLBACK;
  } else {
    const bare = value || name;
    Component = bare ? lucideForLegacyOrKebab(bare) : TAG_FALLBACK;
  }

  return (
    <Component
      size={size}
      stroke={stroke}
      strokeWidth={strokeWidth}
      fill={fill}
      className={className}
      style={style}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    />
  );
}
